import React, { useCallback, useEffect, useState } from "react";
import styles from "./LicenceKeyCard.module.css";
import { track, EVENTS } from "../../utils/analyticsService";
import { copyText } from "../../utils/gift";
import { isIndia } from "../../utils/market";

const REVEALED_KEY = "fleek_codes_revealed";

// code_revealed is "first shown": once per order per browser, not per render.
const firstReveal = (orderId) => {
  if (!orderId) return false;
  try {
    const seen = JSON.parse(localStorage.getItem(REVEALED_KEY) || "[]");
    if (seen.includes(String(orderId))) return false;
    localStorage.setItem(REVEALED_KEY, JSON.stringify([...seen, String(orderId)].slice(-100)));
    return true;
  } catch {
    return false;
  }
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/**
 * The ink licence-key card, from design/Purchase Flow.dc.html. Used on the
 * post-payment screen ("Your licence key" + activation link) and on order
 * detail ("Licence key"). With no key yet, renders the amber activating
 * state the design shows for hand-provisioned plans.
 *
 * `tracking` ({ order_id, plan_id, brand }) feeds code_revealed, code_copied
 * and redeem_link_opened. The key itself is never sent.
 */
export const LicenceKeyCard = ({ licenceKey, activationUrl, label = "Licence key", showPending = true, tracking = {} }) => {
  // idle | copied | failed. "failed" is real: in-app browsers (WhatsApp,
  // Instagram) can refuse both clipboard routes, and a button that silently
  // does nothing reads as broken.
  const [copyState, setCopyState] = useState("idle");
  const copied = copyState === "copied";
  const hasKey = !!licenceKey;
  const orderId = tracking.order_id ?? null;

  useEffect(() => {
    if (!hasKey || !firstReveal(orderId)) return;
    track(EVENTS.CODE_REVEALED, {
      order_id: orderId,
      plan_id: tracking.plan_id ?? null,
      delivery_type: activationUrl ? "code_with_link" : "code",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey, orderId]);

  const copy = useCallback(async () => {
    if (!licenceKey) return;
    track(EVENTS.CODE_COPIED, { order_id: orderId });
    const ok = await copyText(licenceKey);
    setCopyState(ok ? "copied" : "failed");
    setTimeout(() => setCopyState("idle"), ok ? 1600 : 4000);
  }, [licenceKey, orderId]);

  if (!licenceKey) {
    if (!showPending) return null;
    return (
      <section className={styles.pending}>
        <div className={styles.pendingHead}>
          <span className={styles.spinner} aria-hidden="true" />
          <span className={styles.pendingTitle}>Activating your licence</span>
        </div>
        <p className={styles.pendingBody}>
          {isIndia() ? (
            <>
              This plan is provisioned by hand, so it takes a little longer. Expect your key within{" "}
              <strong>2 hours</strong> — we'll WhatsApp you the moment it's ready. No need to keep this
              open.
            </>
          ) : (
            <>
              This plan is provisioned by hand. Expect your details within <strong>24 hours</strong> — we'll
              email you the moment they're ready. No need to keep this open.
            </>
          )}
        </p>
      </section>
    );
  }

  const host = activationUrl ? hostOf(activationUrl) : "";

  return (
    <section className={styles.card}>
      <span className={styles.label}>{label}</span>
      <div className={styles.row}>
        <span className={styles.key}>{licenceKey}</span>
        <button
          type="button"
          className={styles.copy}
          onClick={copy}
          aria-label={copied ? "Copied" : "Copy licence key"}
        >
          <span className={styles.copyText}>{copied ? "Copied" : "Copy"}</span>
          {copied ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 9h11v11H9z" />
              <path d="M15 5H4v11" />
            </svg>
          )}
        </button>
      </div>
      {copyState === "failed" && (
        <p className={styles.copyHint} role="status">
          Couldn't copy here. Press and hold the code to copy it.
        </p>
      )}
      {activationUrl && (
        <a
          className={styles.activate}
          href={activationUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track(EVENTS.REDEEM_LINK_OPENED, { order_id: orderId, brand: tracking.brand || host || null })}
        >
          {host ? `Activate on ${host}` : "Activate now"}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7" />
            <path d="M9 7h8v8" />
          </svg>
        </a>
      )}
    </section>
  );
};

export default LicenceKeyCard;
